<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">
            Detalhes da Campanha
          </h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Acompanhe os disparos, filtre resultados e visualize o desempenho das campanhas.
          </p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row">
          <button type="button"
            class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
            @click="voltarParaCampanhas">
            <svg class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600"
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Voltar
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">
            ID
          </p>
          <div class="mt-2 break-all text-lg font-semibold text-gray-900">
            {{ campanha && campanha._id ? campanha._id : '—' }}
          </div>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm md:col-span-2">
          <p class="text-sm font-medium text-gray-500">
            Nome da campanha
          </p>
          <div class="mt-2 truncate text-lg font-medium text-gray-900">
            {{ campanha && campanha.nome ? campanha.nome : '—' }}
          </div>
        </div>

      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <UiCard v-for="card in cards" :key="card.title"
          class="transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">
                {{ card.title }}
              </p>

              <h2 class="mt-2 text-2xl font-medium text-gray-900">
                {{ card.value }}
              </h2>
            </div>

            <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
              <component :is="getCardIconComponent(card.icon)" :class="['h-5 w-5', card.color]" />
            </div>
          </div>
        </UiCard>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div class="flex flex-col gap-1 md:col-span-2 xl:col-span-1">
          <label class="text-sm font-medium text-gray-500">
            Telefone
          </label>
          <div class="relative">
            <input
              v-model.trim="telefoneFiltro"
              type="tel"
              inputmode="numeric"
              autocomplete="tel"
              placeholder="Digite o número do telefone"
              class="h-11 w-full rounded-xl border border-gray-200 bg-white pl-4 pr-16 text-sm text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
            />

            <button
              v-if="telefoneFiltro"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
              @click="limparFiltroTelefone"
            >
              Limpar
            </button>
          </div>
        </div>

        <div class="flex flex-col gap-1 md:col-span-2 xl:col-span-1">
          <label class="text-sm font-medium text-gray-500">
            Exibir
          </label>
          <label
            class="flex h-11 cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 shadow-sm">
            <input v-model="falhas" type="checkbox" class="h-4 w-4 rounded" />
            <span class="text-sm font-medium text-gray-700">
              Apenas falhas
            </span>
          </label>
        </div>

        <div class="flex items-end gap-2 md:col-span-4 xl:col-span-2 xl:justify-end">
          <UiButton class="w-full xl:w-auto" variant="ghost" @click="exportarAnalitico">
            <template #icon>
              <HeroArrowDownTray
                class="h-5 w-5 text-green-600 transition-transform duration-200 group-hover:translate-y-0.5 group-hover:text-green-700" />
            </template>
            <span class="text-sm font-medium text-gray-700">Download analítico</span>
          </UiButton>
        </div>
      </div>

      <div class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">IDX</th>
                <th class="px-6 py-4 text-left font-medium">Telefone</th>
                <th class="px-6 py-4 text-left font-medium">Mensagem</th>
                <th class="px-6 py-4 text-left font-medium">Cod Cliente</th>
                <th class="px-6 py-4 text-left font-medium">Data Agendada</th>
                <th class="px-6 py-4 text-left font-medium">Data Disparada</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in detalhesPaginados" :key="item._id || item.idx"
                class="border-b border-gray-100 transition hover:bg-slate-50">
                <td class="px-6 py-5 font-medium text-gray-700">
                  {{ item.idx }}
                </td>
                <td class="px-6 py-5 text-gray-700">
                  {{ item.telefone }}
                </td>
                <td class="px-6 py-5 text-gray-700">
                  <div class="flex max-w-xs flex-col gap-2 xl:max-w-sm group">
                    <div class="truncate text-gray-700" :title="mensagemCompleta(item)">
                      {{ resumoMensagem(item) }}
                    </div>

                    <a
                      v-if="item.arquivo && item.arquivo.filename"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 shadow-sm transition hover:bg-blue-100 hover:border-blue-300"
                      :href="`${api}/upload?mimetype=${encodeURIComponent(item.arquivo.mimetype || 'application/octet-stream')}&filename=${encodeURIComponent(item.arquivo.filename)}&folder=arquivosWhats`"
                      :title="`Baixar: ${item.arquivo.filename}`"
                    >
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8m0 8l-4-2m4 2l4-2" />
                      </svg>
                    </a>
                  </div>
                </td>
                <td class="px-6 py-5 text-gray-700">
                  {{ item.idCustom || item.cod || '-' }}
                </td>
                <td class="px-6 py-5 text-gray-700">
                  {{ formatDate(item.dataHoraDisparo || item.agendada) }}
                </td>
                <td class="px-6 py-5 text-gray-700">
                  {{ formatDate(item.data || item.disparada) }}
                </td>
                <td class="px-6 py-5 text-center">
                  <UiBadge
                    :type="isFalha(item) ? 'danger' : (['ENTREGUE', 'VISUALIZADO', 'ENVIADO', 'ENVIADO PARA FILA'].includes(traduzStatus(item)) ? 'success' : 'default')">
                    {{ traduzStatus(item) }}
                  </UiBadge>
                </td>
              </tr>

              <tr v-if="detalhesPaginados.length === 0">
                <td colspan="6" class="px-6 py-12 text-center text-sm text-gray-400">
                  Nenhum registro encontrado com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :current-page="paginaAtual"
        :total-pages="totalPaginas"
        :total-items="total"
        :page-size="limit"
        item-label="registros"
        @paginate="setPage"
      />
    </div>

    
  </div>
</template>

<script>
import moment from "moment";
import AppPagination from '@/components/AppPagination.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiCard from '@/components/ui/UiCard.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import {
  HeroArrowRightOnRectangle,
  HeroArrowDownTray,
  HeroChartBar,
  HeroCheckCircle,
  HeroXCircle,
  HeroBars3
} from '@/components/icons/heroicons'

export default {
  components: { AppPagination, UiButton, UiCard, UiBadge, HeroArrowRightOnRectangle, HeroArrowDownTray, HeroChartBar, HeroCheckCircle, HeroXCircle, HeroBars3 },
  data() {
    return {
      limit: 50,
      falhas: false,
      skip: 0,
      total: 0,
      route: "campanhas",
      api: window.location.hostname === "uno.trestto.com.br" ? "https://uno.trestto.com.br/api" : process.env.VUE_APP_API,
      campanha: null,
      campanhaStatus: null,
      resumoStatusCarregado: false,
      lista: [],
      telefoneFiltro: '',
      telefoneBuscaTimer: null,
      paginaAtual: 1,
      moment: moment,
    
    };
  },
  computed: {
    cards() {
      const total = this.campanha && this.campanha.total ? this.campanha.total : this.total;
      const enviados = this.campanha && this.campanha.totalEnviado ? this.campanha.totalEnviado : this.contarStatus(this.$store.getters.rotuloEnvio('ENVIADO', 'ENVIADO PARA FILA'));
      const falhas = this.campanha && this.campanha.totalFalhas ? this.campanha.totalFalhas : this.contarFalhas();

      return [
        {
          title: 'Total',
          value: this.formatNumber(total),
          icon: 'menu',
          color: 'text-blue-500'
        },
        {
          title: this.$store.getters.rotuloEnvio("Enviados", "Enviados para fila"),
          value: this.formatNumber(enviados),
          icon: 'paper-airplane',
          color: 'text-green-500'
        },
        {
          title: 'Falhas',
          value: this.formatNumber(falhas),
          icon: 'exclamation-triangle',
          color: 'text-red-500'
        },
        {
          title: 'Agendada por',
          value: this.campanha && this.campanha.login && this.campanha.login.nome ? this.campanha.login.nome : 'Usuário',
          icon: 'user',
          color: 'text-indigo-500'
        }
      ];
    },
    detalhesFiltrados() {
      return this.lista;
    },
    detalhesPaginados() {
      return this.lista;
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    }
  },
  watch: {
    telefoneFiltro() {
      this.paginaAtual = 1;
      clearTimeout(this.telefoneBuscaTimer);
      this.telefoneBuscaTimer = setTimeout(() => {
        this.start(1);
      }, 400);
    },
    falhas() {
      this.paginaAtual = 1;
      this.start(1);
    }
  },
  methods: {
    async setPage(page) {
      this.paginaAtual = page;
      await this.start(page);
    },
    limparFiltroTelefone() {
      this.telefoneFiltro = '';
    },
    getCardIconComponent(iconName) {
      switch (iconName) {
        case 'menu':
          return 'HeroBars3';
        case 'paper-airplane':
          return 'HeroArrowRightOnRectangle';
        case 'exclamation-triangle':
          return 'HeroXCircle';
        case 'user':
          return 'HeroCheckCircle';
        case 'chart-bar':
          return 'HeroChartBar';
        default:
          return 'HeroBars3';
      }
    },
    formatId(id) {
      return id ? `${id.slice(0, 8)}....` : '—';
    },
    formatNumber(value) {
      return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
    },
    formatDate(value) {
      if (!value) return '-';
      return moment(value).format('DD/MM/YYYY HH:mm:ss');
    },
    mensagemCompleta(item) {
      if (item && item.mensagem) return String(item.mensagem);
      return this.resumoMensagem(item);
    },
    resumoMensagem(item) {
      const mensagem = item && item.mensagem ? String(item.mensagem) : '';
      return mensagem.length > 55 ? `${mensagem.slice(0, 55)}...` : mensagem || '-';
    },
    traduzStatus(item) {
      if (!item) return 'PENDENTE';
      if (item.status) return this.traduzDlr(item.status);
      if (item.result && item.temzap && item.enviou) return 'ENVIADO PARA FILA';
      if (item.bloqueado) return 'BLOQUEADO';
      if (item.result && (!item.temzap || !item.enviou)) return 'FALHOU';
      if (!item.result) return 'PROGRAMADA';
      return 'PENDENTE';
    },
    traduzDlr(status) {
      if (status === -1) return 'PENDENTE';
      if (status === 0) return 'NÚMERO INVÁLIDO';
      if (status === 1) return this.$store.getters.rotuloEnvio('ENVIADO', 'ENVIADO PARA FILA');
      if (status === 3) return 'ENTREGUE';
      if (status === 4) return 'VISUALIZADO';
      if (status === 9) return 'NÃO ENVIADO';
      return 'PENDENTE';
    },
    getStatusClass(item) {
      const status = this.traduzStatus(item);
      if (status === 'FALHOU' || status === 'NÃO ENVIADO' || status === 'BLOQUEADO' || status === 'NÚMERO INVÁLIDO') {
        return 'bg-red-100 text-red-600';
      }
      if (status === 'ENTREGUE' || status === 'VISUALIZADO' || status === 'ENVIADO' || status === 'ENVIADO PARA FILA') {
        return 'bg-green-100 text-green-700';
      }
      if (status === 'PROGRAMADA' || status === 'PENDENTE') {
        return 'bg-gray-100 text-gray-700';
      }
      return 'bg-blue-100 text-blue-700';
    },
    isFalha(item) {
      return this.traduzStatus(item) === 'FALHOU' || this.traduzStatus(item) === 'NÃO ENVIADO' || this.traduzStatus(item) === 'BLOQUEADO' || this.traduzStatus(item) === 'NÚMERO INVÁLIDO';
    },
    contarStatus(status) {
      return this.lista.filter(item => this.traduzStatus(item) === status).length;
    },
    contarFalhas() {
      return this.lista.filter(item => this.isFalha(item)).length;
    },
    voltarParaCampanhas() {
      this.$router.push({ name: 'Campanhas' });
    },
    exportarAnalitico() {
      const id = this.$route.params.id ? this.$route.params.id.match(/^[0-9a-fA-F]{24}$/)[0] : '';
      if (!id) return;
      window.open(`${process.env.VUE_APP_API}/v1/campanhasLZV2/download-resultado-url/${id}?token=${this.$store.state.token}`, '_blank');
    },
    async addLimit() {
      if (this.$route.query && this.$route.query.api === 'true') {
        this.limit += 20;
        this.start();
        return;
      }
      if (this.lista.length + 2000 > this.total) this.limit += this.total - this.lista.length;
      else this.limit += 2000;

      await this.start();
    },
    async start(page = this.paginaAtual) {
      const id = this.$route.params.id ? this.$route.params.id.match(/^[0-9a-fA-F]{24}$/)[0] : '';
      if (id) {
        const pagina = page || 1;
        this.paginaAtual = pagina;
        this.skip = (pagina - 1) * this.limit;
        const telefone = String(this.telefoneFiltro || '').replace(/\D/g, '');
        const query = [
          `limit=${encodeURIComponent(this.limit)}`,
          `skip=${encodeURIComponent(this.skip)}`,
          `falhas=${encodeURIComponent(this.falhas)}`,
          `telefone=${encodeURIComponent(telefone)}`,
          'registrarRTPotencial=1'
        ].join('&');
        const req = await this.$http.get(`/v1/campanhasLZV2/${id}/contatos?${query}`);
        this.campanha = req.data.campanha;
        this.lista = req.data.data || [];
        this.total = req.data.total || 0;

        if (this.$store.state.user.neon && !this.resumoStatusCarregado) {
          const resp = await this.$http.get(`/v1/campanhasLZV2/${id}/resumostatus`);
          this.campanhaStatus = resp.data.result;
          this.resumoStatusCarregado = true;
        }
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.telefoneBuscaTimer);
  },
  beforeMount() {
    this.start();
  }
};
</script>

<style scoped></style>
